/**
 * Browser-local blinking clock on R1AS stove display — docs/R1AS_STOVE_CLOCK.md
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { R1AS_STOVE_DISPLAY } from '../../data/r1asStoveDisplay';

const W = 256;
const H = 96;

function formatParts(date) {
  const fmt = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const parts = fmt.formatToParts(date);
  let hour = '';
  let minute = '';
  let dayPeriod = '';
  let literal = ':';
  parts.forEach((p) => {
    if (p.type === 'hour') hour = p.value;
    else if (p.type === 'minute') minute = p.value;
    else if (p.type === 'dayPeriod') dayPeriod = p.value;
    else if (p.type === 'literal' && /[:．.]/.test(p.value)) literal = p.value;
  });
  return { hour, minute, dayPeriod, literal };
}

/**
 * @param {{ enabled?: boolean, pose?: typeof R1AS_STOVE_DISPLAY }} props
 */
export default function LabStoveClock({
  enabled = true,
  pose = R1AS_STOVE_DISPLAY,
}) {
  const matRef = useRef(null);
  const blinkRef = useRef(true);
  const lastMinRef = useRef(-1);

  const { canvas, ctx, texture } = useMemo(() => {
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const context = c.getContext('2d');
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    return { canvas: c, ctx: context, texture: tex };
  }, []);

  useEffect(
    () => () => {
      texture.dispose();
    },
    [texture]
  );

  const paint = (showColon) => {
    if (!ctx) return;
    const now = new Date();
    const { hour, minute, dayPeriod, literal } = formatParts(now);

    ctx.fillStyle = '#0a0c0e';
    ctx.fillRect(0, 0, W, H);

    // subtle inset
    ctx.strokeStyle = '#1a2228';
    ctx.lineWidth = 4;
    ctx.strokeRect(3, 3, W - 6, H - 6);

    ctx.fillStyle = '#f0a030';
    ctx.font = '600 52px "JetBrains Mono", "SF Mono", Menlo, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(240, 160, 48, 0.55)';
    ctx.shadowBlur = 10;

    const midY = H * 0.52;
    const colon = showColon ? literal || ':' : ' ';
    const main = `${hour}${colon}${minute}`;
    ctx.fillText(main, W * 0.42, midY);

    if (dayPeriod) {
      ctx.font = '500 22px "JetBrains Mono", "SF Mono", Menlo, monospace';
      ctx.shadowBlur = 4;
      ctx.fillText(dayPeriod.toUpperCase(), W * 0.82, midY + 2);
    }

    ctx.shadowBlur = 0;
    texture.needsUpdate = true;
    lastMinRef.current = now.getMinutes();
  };

  useEffect(() => {
    if (!enabled) return undefined;
    paint(true);
    const id = window.setInterval(() => {
      blinkRef.current = !blinkRef.current;
      paint(blinkRef.current);
    }, 500);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paint closes over canvas
  }, [enabled, texture]);

  // Catch minute rollover between blinks
  useFrame(() => {
    if (!enabled) return;
    const m = new Date().getMinutes();
    if (m !== lastMinRef.current) paint(blinkRef.current);
  });

  if (!enabled || !pose) return null;

  const [px, py, pz] = pose.position;
  const [rx, ry, rz] = pose.rotation || [0, 0, 0];

  return (
    <mesh
      position={[px, py, pz]}
      rotation={[rx, ry, rz]}
      userData={{ annotateIgnore: true, stoveClock: true }}
    >
      <planeGeometry args={[pose.width, pose.height]} />
      <meshBasicMaterial
        ref={matRef}
        map={texture}
        toneMapped={false}
        transparent={false}
        side={THREE.FrontSide}
      />
    </mesh>
  );
}
